import React, { useState, useEffect } from "react";
import axiosInstance from '../services/axiosInstance';
import GlobalTable from "../components/GlobalTable";
import { useLazyTable } from "../hooks/useLazyTable";
import { handleApiError, handleApiSuccess } from "../components/apiErrorHandler";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from 'primereact/inputtextarea';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';       

const ProductAdmin = () => {
    const [product, setProduct] = useState({
        code: "",
        name: "",
        common_name: "",
        strength: "",
        description: "",
        ingredients: "",
        uses: "",
        directions: "",
        warnings: "",
        is_expiring: true,
        pharmacist_only: false,
        discountable: true,   
        organization: "b835b027-72ba-4c99-af9c-c8c6c1e1c732",
    });

    const [products, setProducts] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const { lazyParams, onPage, onSort, onFilter, queryParams } = useLazyTable({ rows: 15 });
    const [dialogVisible, setDialogVisible] = useState(false);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [dialogName, setDialogName] = useState('Create Product');
    const [loading, setLoading] = useState(true);

    const uses = [
        {label:'Tablet',value:'Tablet'},
        {label:'Capsule',value:'Capsule'},
        {label:'Injection',value:'Injection'},
        {label:'Syrup',value:'Syrup'},
        {label:'Cream',value:'Cream'},
        {label:'Liquid',value: 'Liquid'},    
        {label:'Other',value: 'Other'},   
    ]

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const productResult = await axiosInstance.get("/api/products/", { params: queryParams });
                setProducts(productResult.data.results);
                setTotalRecords(productResult.data.count);
                setLoading(false);
            } catch (error) {
                handleApiError(error);
                setLoading(false);
            }
        };
        fetchData();
    }, [queryParams]);

    const handleChange = (e, field) => {
        const value = e.target ? e.target.value : e.value;
        setProduct((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleRowClick = (dep) => {
        setProduct(dep);
        setIsReadOnly(true);  // Set to read-only mode
        setDialogVisible(true);
        setDialogName('Product Details')
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const result = await axiosInstance.post("/api/products/", product);
            setProducts([...products, result.data]);
            setProduct({
                code: "",
                name: "",
                common_name: "",
                strength: "",
                description: "",
                ingredients: "",
                uses: "",
                directions: "",
                warnings: "",
                is_expiring: true,
                pharmacist_only: false,
                discountable: true,   
                expiration_remind: "",
                organization: "b835b027-72ba-4c99-af9c-c8c6c1e1c732",
            });
            handleApiSuccess(`Product created: ${result.data.name}`);
        } catch (error) {
            handleApiError(error);
        }
    };

    const handleRowEditComplete = async (e) => {
        const { newData, index } = e;

        try {
            const updatedProduct = await axiosInstance.put(`/api/products/${newData.id}/`, newData);
            const updatedProducts = [...products];
            updatedProducts[index] = updatedProduct.data;
            setProducts(updatedProducts);
            handleApiSuccess("Product updated successfully!");
        } catch (error) {
            handleApiError(error);
        }
    };

    const allowEdit = (roDatas) => {
        return !roDatas.public_flg;
    };


    const columns = [
            {   Header: "Code", 
                accessor: "code", 
                sortable: true, 
                filtered:true, 
                bodyTemplate: (rowData) => (
                    <a
                        href="/products/#"
                        onClick={(e) => {
                            e.preventDefault();
                            handleRowClick(rowData);
                        }}
                    >
                        {rowData.code}
                    </a>
                ),
            },
            { Header: "Name", accessor: "name", sortable: true, filtered:true, },
            { Header: "Type", accessor: "uses",},
            { Header: "Common Name", accessor: "common_name", sortable: true, filtered:true, },
            { 
                Header: "Expiring", 
                accessor: "is_expiring", 
                filtered:true, 
                bodyTemplate: (rowData) => (
                                <Checkbox checked={rowData.is_expiring === true || rowData.is_expiring === 'Y'} disabled />
                            ),
            },
            { Header: "Expiration Remind Days", accessor: "expiration_remind", sortable: true, filtered:true, },
            { 
                Header: "Admin Only", 
                accessor: "pharmacist_only", 
                filtered:true,
                bodyTemplate: (rowData) => (
                                <Checkbox checked={rowData.pharmacist_only === true || rowData.pharmacist_only === 'Y'} disabled />
                            ),
            },
            { 
                Header: "Discountable", accessor: "discountable",
                bodyTemplate: row => (<Checkbox checked={row.discountable === true || row.discountable === 'Y'} disabled />)
            },
            { Header: "Strength", accessor: "strength", sortable: true, filtered:true, },
            { Header: "Description", accessor: "description", sortable: true, filtered:true, },
            { Header: "Ingredients", accessor: "ingredients", sortable: true, filtered:true, },
            { Header: "Directions", accessor: "directions",},
            { Header: "Warnings", accessor: "warnings",},
        ];

    const handleAddClick = () => {
        setProduct({
            code: "",
            name: "",
            common_name: "",
            strength: "",
            description: "",
            ingredients: "",
            uses: "",
            directions: "",
            warnings: "",
            is_expiring: true,
            pharmacist_only: false,
            discountable: true,   
            organization: "b835b027-72ba-4c99-af9c-c8c6c1e1c732",
        });
        setIsReadOnly(false);
        setDialogName('Create Product')
        setDialogVisible(true);
    };

    const generateProductCode = async () => {
        try {
            const res = await axiosInstance.get("/api/products/generate-code/");
            setProduct((prev) => ({
                ...prev,
                code: res.data.code,
            }));
        } catch (err) {
            handleApiError(err);
        }
    };

    const headerAction = (
        <Button type="button" icon="pi pi-plus" label=" Add Product" outlined onClick={handleAddClick} className="w-full sm:w-auto" />
    );

    return (
        <div>
            <div className="containers">
                <GlobalTable
                    columns={columns}
                    data={products}
                    allowEdit={allowEdit}
                    tabletitle="Product List"
                    lazy
                    totalRecords={totalRecords}
                    lazyParams={lazyParams}
                    onPage={onPage}
                    onSort={onSort}
                    onFilter={onFilter}
                    customEditors={{
                        uses: (options) => (
                            <Dropdown
                                value={options.rowData.uses} 
                                options={uses}
                                onChange={(e) => options.editorCallback(options.rowData.uses=e.value)}
                                placeholder="Select Type"
                            />
                        ),
                        pharmacist_only: (options) => (
                            <Checkbox
                                inputId="isadmin"
                                checked={options.rowData.pharmacist_only === true || options.rowData.pharmacist_only === 'Y'}
                                onChange={(e) => options.editorCallback(e.checked)}
                            />
                        ),
                        is_expiring: (options) => (
                            <Checkbox
                                inputId="isexpiring"
                                checked={options.rowData.is_expiring === true || options.rowData.is_expiring === 'Y'}
                                onChange={(e) => options.editorCallback(e.checked)}
                            />
                        ),
                    }}
                    onRowEditComplete={handleRowEditComplete}
                    headerAction={headerAction}
                    loading={loading}
                />
            </div>
            <Dialog
                header={dialogName}
                visible={dialogVisible}
                onHide={() => setDialogVisible(false)}
                modal
                style={{ width: '50rem', borderRadius: '12px', padding: '.5rem'}} 
                breakpoints={{ '960px': '75vw', '641px': '90vw' }}
                className="p-fluid"
            >
                <form id="dialogForm" onSubmit={handleSubmit}>
                <div style={{ borderRadius: '12px', padding: '1rem' }}>
                    <div className="formgrid grid">
                        <div className="field col" >
                            <label className="font-bold" htmlFor="product_code" >Product Code:</label>
                            <div className="p-inputgroup">
                            <InputText
                                id="product_code"
                                value={product.code}
                                onChange={(e) => handleChange(e, "code")}
                                placeholder="Enter Product Code"
                                required
                                readOnly={isReadOnly}
                            />
                            <span 
                                className={`p-inputgroup-addon ${isReadOnly ? 'disabled' : ''}`} 
                                onClick={generateProductCode}
                                style={{
                                    cursor: isReadOnly ? 'not-allowed' : 'pointer',
                                    opacity: isReadOnly ? 0.5 : 1,
                                  }}
                                >
                                    <i className="pi pi-cog" />
                            </span>
                            </div>

                        </div>
                        <div className="field col">
                            <label className="font-bold" htmlFor="product_name">Product Name:</label>
                            <InputText
                                id="product_name"
                                value={product.name}
                                onChange={(e) => handleChange(e, "name")}
                                placeholder="Enter Product Name"
                                required
                                readOnly={isReadOnly}
                            />
                        </div>
                        <div className="field col">
                            <label className="font-bold" htmlFor="uses">Type:</label>
                            <Dropdown
                                id="uses"
                                value={product.uses}
                                options={uses}
                                onChange={(e) => handleChange(e, "uses")}
                                placeholder="Select Type"
                                showClear
                                required
                                disabled={isReadOnly}
                            />
                        </div>
                        </div>
                    <div className="formgrid grid">
                        <div className="col">
                            <Checkbox
                                inputId="isexpiring"
                                checked={product.is_expiring}
                                onChange={e => setProduct(prev => ({
                                    ...prev,
                                    is_expiring: e.checked
                                }))}
                                disabled={isReadOnly}
                                />
                                <label htmlFor="isexpiring" className="font-bold">Expiring product</label>
                        </div>
                       <div className="col">
                            <Checkbox
                                inputId="isadmin"
                                checked={product.pharmacist_only}
                                onChange={e => setProduct(prev => ({
                                    ...prev,
                                    pharmacist_only: e.checked
                                }))}
                                disabled={isReadOnly}
                                />
                            <label htmlFor="isadmin" className="font-bold">Issue Through Admin</label>    
                        </div> 
                        <div className="col">
                            <Checkbox
                                inputId="discountable"
                                checked={!!product.discountable}
                                onChange={e => setProduct(prev => ({ ...prev, discountable: e.checked }))}
                                disabled={isReadOnly}
                            />
                            <label htmlFor="discountable" className="font-bold">Discountable</label>
                        </div>
                    </div><p></p>
                    <div className="formgrid grid">
                        <div className="field col">
                            <label className="font-bold" htmlFor="expiration_remind">Expiry Remind Days:</label>
                            <InputText
                                id="expiration_remind"
                                value={product.expiration_remind}
                                onChange={(e) => handleChange(e, "expiration_remind")}
                                placeholder="Expiry Remind Days"
                                readOnly={isReadOnly}
                            />
                        </div>
                        <div className="field col">
                            <label className="font-bold" htmlFor="common_name">Generic Name:</label>
                            <InputText
                                id="common_name"
                                value={product.common_name}
                                onChange={(e) => handleChange(e, "common_name")}
                                placeholder="Enter Common Name"
                                readOnly={isReadOnly}
                            />
                        </div>
                        <div className="field col">
                            <label className="font-bold" htmlFor="strength">Strength:</label>
                            <InputText
                                id="strength"
                                value={product.strength}
                                onChange={(e) => handleChange(e, "strength")}
                                placeholder="Enter Strength"
                                readOnly={isReadOnly}
                            />
                        </div>
                    </div>
                    <div className="formgrid grid">
                        <div className="field col">
                            <label className="font-bold" htmlFor="product_desc">Description:</label>
                            <InputTextarea
                                id="product_desc"
                                value={product.description || ''}
                                onChange={(e) => handleChange(e, "description")}
                                placeholder="Enter Product Description"
                                readOnly={isReadOnly}
                            />
                        </div>
                        <div className="field col">
                            <label className="font-bold" htmlFor="ingredients">Ingredients:</label>
                            <InputTextarea
                                id="ingredients"
                                value={product.ingredients || ''}
                                onChange={(e) => handleChange(e, "ingredients")}
                                placeholder="Enter Ingredients"
                                readOnly={isReadOnly}
                            />                          
                        </div>
                    </div>
                    <div className="formgrid grid">
                        <div className="field col">
                            <label className="font-bold" htmlFor="directions">Directions:</label>
                            <InputTextarea
                                id="directions"
                                value={product.directions || ''}
                                onChange={(e) => handleChange(e, "directions")}
                                placeholder="Enter Directions"
                                readOnly={isReadOnly}
                            />                          
                        </div>
                        <div className="field col">
                            <label className="font-bold" htmlFor="warnings">Warnings:</label> 
                            <InputTextarea
                                id="warnings"
                                value={product.warnings || ''}
                                onChange={(e) => handleChange(e, "warnings")}
                                placeholder="Enter Warnings"
                                readOnly={isReadOnly}
                            />                         
                        </div>
                    </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                        <Button label="Save" icon="pi pi-check" form="dialogForm" type='submit' style={{borderRadius:"6px", maxWidth: "120px"}} disabled={isReadOnly}/>
                        <Button label="Cancel" icon="pi pi-times" outlined onClick={(e) => {setDialogVisible(false); e.preventDefault();}}  style={{borderRadius:"6px", maxWidth: "120px"}} />
                    </div> 
                    </form>
               </Dialog> 
        </div>
    );
};

export default ProductAdmin;